import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiExternalLink } from 'react-icons/fi';
import { FaPencilAlt, FaBox, FaDollarSign } from 'react-icons/fa';
import { CURRENCY_SYMBOLS } from '../../utils/currencySymbols';

const ItemDetailDrawer = ({ isOpen, onClose, item, onUpdate, onSold, salesHistory = [] }) => {
  if (!item) return null;

  const getStockStatus = (qty) => {
    if (qty === 0) return { color: 'text-red-600', label: 'Out of Stock' };
    if (qty < 3) return { color: 'text-yellow-600', label: 'Low Stock' };
    return { color: 'text-green-600', label: 'In Stock' };
  };

  const stockStatus = getStockStatus(item.quantity || 0);
  const priceCurrencySymbol = CURRENCY_SYMBOLS[item.priceCurrency || 'USD'];
  const costPriceCurrencySymbol = CURRENCY_SYMBOLS[item.costPriceCurrency || 'USD'];
  
  // Filter sales for this specific item
  const itemSales = salesHistory.filter(sale => sale.itemId === item.id);
  const totalSold = itemSales.reduce((sum, sale) => sum + sale.quantitySold, 0);
  const totalRevenue = itemSales.reduce((sum, sale) => sum + (sale.soldPrice * sale.quantitySold), 0);
  
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white dark:bg-gray-800 
              shadow-xl z-[70] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex justify-between items-center z-10">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Item Details</h3>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="px-4 pt-6 pb-10 space-y-6">
              {/* Product Image or Icon */}
              {item.imageUrl ? (
                <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden relative">
                  <img 
                    src={item.imageUrl} 
                    alt={item.name}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="absolute inset-0 bg-gradient-to-r from-teal-400 to-cyan-600 flex items-center justify-center"
                    style={{ display: 'none' }}
                  >
                    <FaBox className="h-20 w-20 text-white" />
                  </div>
                </div>
              ) : (
                <div className="w-full h-48 bg-gradient-to-r from-teal-400 to-cyan-600 rounded-lg flex items-center justify-center">
                  <FaBox className="h-20 w-20 text-white" />
                </div>
              )}

              {/* Title */}
              <div>
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {item.name}
                </h4>
                {item.productUrl && (
                  <a
                    href={item.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 
                      text-white rounded-lg hover:shadow-lg hover:from-teal-600 hover:to-cyan-700 
                      transition-all duration-300 transform hover:scale-105 font-medium text-sm"
                  >
                    <FiExternalLink className="mr-2 h-4 w-4" />
                    View Product Page
                  </a>
                )}
              </div>

              {/* Price & Stock */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Price</p>
                  <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                    {priceCurrencySymbol}{parseFloat(item.price || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Stock</p>
                  <p className={`text-2xl font-bold ${stockStatus.color}`}>
                    {item.quantity || 0}
                  </p>
                  <p className={`text-xs ${stockStatus.color}`}>
                    {stockStatus.label}
                  </p>
                </div>
              </div>

              {/* Item Details */}
              <div className="space-y-3">
                {item.category && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Category</p>
                    <p className="text-gray-900 dark:text-white">{item.category}</p>
                  </div>
                )}
                {item.sku && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">SKU</p>
                    <p className="text-gray-900 dark:text-white font-mono">{item.sku}</p>
                  </div>
                )}
                {item.costPrice && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Cost Price</p>
                    <p className="text-gray-900 dark:text-white">
                      {costPriceCurrencySymbol}{parseFloat(item.costPrice).toFixed(2)}
                    </p>
                    {item.price && item.costPrice && item.priceCurrency === item.costPriceCurrency && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Profit Margin: {priceCurrencySymbol}{(parseFloat(item.price) - parseFloat(item.costPrice)).toFixed(2)} 
                        &nbsp;({(((parseFloat(item.price) - parseFloat(item.costPrice)) / parseFloat(item.price)) * 100).toFixed(1)}%)
                      </p>
                    )}
                  </div>
                )}
                {item.supplier && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Supplier</p>
                    <p className="text-gray-900 dark:text-white">{item.supplier}</p>
                  </div>
                )}
                {item.location && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                    <p className="text-gray-900 dark:text-white">{item.location}</p>
                  </div>
                )}
                {item.notes && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Notes</p>
                    <p className="text-gray-900 dark:text-white text-sm whitespace-pre-wrap">
                      {item.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Sales History */}
              {itemSales.length > 0 && (
                <div className="pt-4 border-t dark:border-gray-700">
                  <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Sales History
                  </h5>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Sold</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {totalSold}
                      </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Revenue</p>
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {priceCurrencySymbol}{totalRevenue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {itemSales.slice(0, 5).map((sale) => (
                      <div 
                        key={sale.saleId} 
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 text-sm"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {sale.quantitySold} × {CURRENCY_SYMBOLS[sale.soldPriceCurrency]}{sale.soldPrice.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(sale.soldAt)}
                            </p>
                          </div>
                          <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {CURRENCY_SYMBOLS[sale.soldPriceCurrency]}{(sale.soldPrice * sale.quantitySold).toFixed(2)}
                          </p>
                        </div>
                        {sale.notes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {sale.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {itemSales.length > 5 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                      +{itemSales.length - 5} more sales
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-4 space-y-3">
                <button
                  onClick={() => {
                    onSold(item);
                    onClose();
                  }}
                  className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-green-600 
                    text-white rounded-lg hover:shadow-lg transition-all font-medium 
                    flex items-center justify-center"
                >
                  <FaDollarSign className="mr-2" />
                  Record Sale
                </button>
                <button
                  onClick={() => {
                    onUpdate(item);
                    onClose();
                  }}
                  className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-cyan-600 
                    text-white rounded-lg hover:shadow-lg transition-all font-medium 
                    flex items-center justify-center"
                >
                  <FaPencilAlt className="mr-2" />
                  Edit Item
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ItemDetailDrawer;

